// Copyright (c) 2026 DMG L&D, LLC. All rights reserved.
// Proprietary and confidential. See LICENSE for terms.

/**
 * Decode a Base64 string (given as UTF-8 bytes) back to raw bytes.
 *
 * Accepts both standard and URL-safe alphabets automatically.
 */

import type { Bytes } from '../types.ts';

export async function base64Decode(input: Bytes): Promise<Bytes> {
  if (!(input instanceof Uint8Array)) {
    throw new Error('base64.decode: input must be a Uint8Array');
  }

  let str = new TextDecoder().decode(input).trim();
  if (str.length === 0) {
    return new Uint8Array(0);
  }

  // Normalize URL-safe to standard
  str = str.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if missing
  const remainder = str.length % 4;
  if (remainder === 2) str += '==';
  else if (remainder === 3) str += '=';

  const LOOKUP = new Uint8Array(128);
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  for (let i = 0; i < ALPHABET.length; i++) {
    LOOKUP[ALPHABET.charCodeAt(i)] = i;
  }

  const outLen = (str.length / 4) * 3
    - (str[str.length - 1] === '=' ? 1 : 0)
    - (str[str.length - 2] === '=' ? 1 : 0);

  const out = new Uint8Array(outLen);
  let j = 0;

  for (let i = 0; i < str.length; i += 4) {
    const a = LOOKUP[str.charCodeAt(i)]!;
    const b = LOOKUP[str.charCodeAt(i + 1)]!;
    const c = LOOKUP[str.charCodeAt(i + 2)]!;
    const d = LOOKUP[str.charCodeAt(i + 3)]!;

    const triplet = (a << 18) | (b << 12) | (c << 6) | d;

    if (j < outLen) out[j++] = (triplet >> 16) & 0xff;
    if (j < outLen) out[j++] = (triplet >> 8) & 0xff;
    if (j < outLen) out[j++] = triplet & 0xff;
  }

  return out;
}
